import { type ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useTranslation } from 'react-i18next';

import { Button } from './Button';
import styles from './Dialog.module.css';
import { CloseIcon } from './icons';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * O documento calcula layout?
 *
 * `offsetParent` e a pergunta certa no navegador e a pergunta errada no
 * `jsdom`, que nao calcula layout e responde `null` para TODO elemento. Com o
 * criterio antigo, a lista de focaveis dentro do dialogo saia com um item — o
 * que ja tinha o foco —, primeiro e ultimo eram o mesmo no, e o laco de Tab
 * devolvia o foco a ele proprio. O teste chamado "prende o foco" passava com o
 * tratamento de Tab inteiro apagado, e o modal esta em producao em materiais,
 * agenda, matriculas, precos e no portao de reautenticacao.
 *
 * A pergunta e feita ao `<body>`, e nao ao elemento: no navegador ele sempre
 * tem retangulo; no `jsdom`, nenhum elemento tem. Assim o comportamento em
 * producao continua exatamente o de antes — quem tem layout responde por
 * `offsetParent` — e a armadilha passa a ser exercitavel onde ela e testada.
 */
function documentHasLayout(): boolean {
  return typeof document !== 'undefined' && document.body.getClientRects().length > 0;
}

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

/**
 * Dialogo modal com foco preso e retorno do foco ao fechar, como a §2.2 exige.
 *
 * Nao usa o elemento `<dialog>` nativo porque `showModal` ainda tem suporte
 * irregular no `jsdom`, e um modal cuja armadilha de foco nao pode ser testada
 * e um modal que quebra em silencio.
 */
export function Dialog({ open, title, onClose, children, footer }: DialogProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const focusableElements = useCallback((): HTMLElement[] => {
    const container = dialogRef.current;
    if (!container) return [];
    const withLayout = documentHasLayout();
    return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => {
      // Escondido por atributo nao entra em ambiente nenhum: e a unica forma de
      // "invisivel" que os dois sabem responder igual.
      if (element.closest('[hidden], [aria-hidden="true"]') !== null) return false;
      if (!withLayout) return true;
      return element.offsetParent !== null || element === document.activeElement;
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const first = focusableElements()[0] ?? dialogRef.current;
    first?.focus();

    return () => {
      // Retorno do foco: sem isso o teclado volta para o inicio do documento e
      // a pessoa perde o lugar onde estava.
      previouslyFocused.current?.focus();
    };
  }, [open, focusableElements]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const elements = focusableElements();
    if (elements.length === 0) {
      event.preventDefault();
      return;
    }

    const first = elements[0];
    const last = elements[elements.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last?.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const { t } = useTranslation();

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('dialog.close')}
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        </div>

        <div>{children}</div>

        {footer ? <div className={styles.actions}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly loading?: boolean;
}

/**
 * Acao destrutiva so acontece depois de confirmacao explicita, e o botao que
 * confirma diz **o que** vai acontecer — nunca apenas "OK". A §2.2 pede
 * confirmacao; um rotulo generico satisfaz a letra e nao o proposito.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {t('action.cancel')}
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{description}</p>
    </Dialog>
  );
}
